import { useEffect, useRef, type ReactNode } from "react";
import { useBatchJobs } from "./useBatchJobs";
import { BatchJobsContext } from "./batchJobsContext";
import { batchApi } from "@services/batchApi";
import { isBatchTerminal } from "@models/batch";
import type { BatchJobEntry } from "@models/batch";

const POLL_INTERVAL_MS = 8000;

/**
 * Shared batch-jobs state + auto-poll loop.
 *
 * Batch state must be shared between the Batch tab (table) and the tab badge
 * count, and the auto-poll must keep running even when the Batch tab is not the
 * active (mounted) tab. Owning both here — above the Tabs — fixes two defects:
 *  - `useBatchJobs` was instantiated twice (view + badge), so the poller
 *    updated a different state instance than the badge read, and history was
 *    fetched twice.
 *  - The poll `setInterval` lived inside the tab panel, which `keepMounted`
 *    unmounts on tab switch, tearing the interval down before it could fire.
 *
 * The loop also runs once immediately (no initial 8s dead time).
 */
export const BatchJobsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const api = useBatchJobs();
  const { update } = api;

  // Keep a ref to the latest jobs so the stable interval reads current state.
  const jobsRef = useRef(api.jobs);
  jobsRef.current = api.jobs;

  useEffect(() => {
    // Poll jobs still in progress OR completed-but-download-not-yet-ready.
    const needsPolling = (j: BatchJobEntry): boolean =>
      !isBatchTerminal(j.status) || (j.status === "completed" && !j.resultsUrl);

    const poll = async (): Promise<void> => {
      const activeJobs = jobsRef.current.filter(needsPolling);
      if (activeJobs.length === 0) return;

      for (const job of activeJobs) {
        try {
          const res = await batchApi.getJobStatus(job.jobId);

          let status: BatchJobEntry["status"] = "pending";
          if (res.global_status === "completed") status = "completed";
          else if (res.global_status === "failed") status = "failed";
          else if (res.global_status === "pending") status = "pending";
          else if (res.global_status === "queued") status = "queued";

          let completedUnits = 0;
          let totalUnits = 0;
          if (res.validation) {
            const reports = Object.values(res.validation);
            completedUnits = reports.reduce(
              (sum, v) => sum + (v.received_count ?? 0),
              0,
            );
            totalUnits = reports.reduce(
              (sum, v) => sum + (v.expected_count ?? 0),
              0,
            );
          }

          const resultsUrl = res.presigned_urls
            ? Object.values(res.presigned_urls).find((url) =>
                url.startsWith("http"),
              )
            : undefined;

          update(job.jobId, {
            status,
            completedUnits,
            ...(totalUnits > 0 ? { totalUnits } : {}),
            resultsUrl,
            error: res.error ?? undefined,
          });
        } catch {
          // Transient error — will retry on the next interval.
        }
      }
    };

    void poll(); // run immediately, then on an interval
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [update]);

  return (
    <BatchJobsContext.Provider value={api}>
      {children}
    </BatchJobsContext.Provider>
  );
};
